import { useCallback, useEffect, useState } from "react";
import api from "../utils/api/axiosInstance";
import { useUserAuth } from "../contexts/UserAuthContext";

/**
 * Server-side Shopper Model (canonical match intent).
 */
export const useShopperModel = (options = {}) => {
  const { autoFetch = true, summaryOnly = false } = options;
  const { user } = useUserAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchModel = useCallback(async () => {
    if (!user) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const path = summaryOnly
        ? "/users/me/shopper-model/summary"
        : "/users/me/shopper-model";
      const res = await api.get(path);
      setData(summaryOnly ? res.data : res.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load shopping model",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user, summaryOnly]);

  useEffect(() => {
    if (autoFetch && user) {
      fetchModel();
    }
  }, [autoFetch, user, fetchModel]);

  const rebuild = useCallback(async () => {
    await api.post("/users/me/shopper-model/rebuild");
    await fetchModel();
  }, [fetchModel]);

  const clearLearning = useCallback(async () => {
    await api.delete("/users/me/shopper-model");
    await fetchModel();
  }, [fetchModel]);

  return {
    shopperModel: summaryOnly ? null : data?.shopperModel,
    summary: summaryOnly ? data : data?.enginePreview,
    enginePreview: data?.enginePreview,
    loading,
    error,
    refetch: fetchModel,
    rebuild,
    clearLearning,
  };
};

export default useShopperModel;
